import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Profile } from '../../Types';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import DataState from '../../Types/DataState';
import DataSource from '../../api';
import Naws, { GetTrainingStatsInput } from '../../api/Naws';
import { StateContext } from '../../GlobalStateStore';
import moment from 'moment';
import { PresetDate } from './types/constants';
import { TrainingStatsResult } from '../../api/Training/schema';
import { ComputedTrainingStats } from './types/types';
import Card from '@amzn/meridian/card';
import Heading from '@amzn/meridian/heading';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import Pie from '@amzn/meridian/pie';
import Legend, { LegendProvider } from '@amzn/meridian/legend';

interface TrainingStatsProps {
  profile: Profile;
}

const TrainingStats: React.FC<TrainingStatsProps> = ({ profile }) => {
  const intl = useIntl();
  const [presetDate, setPresetDate] = useState<PresetDate>(
    PresetDate.TRAILING_WEEK
  );
  const [trainingStats, setTrainingStats] = useState<TrainingStatsResult>();
  const [trainingStatsState, setTrainingStatsState] = useState(
    DataState.LOADING
  );

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { cscEntityPrefix } = inspireOptions.data;
  const apiNaws: typeof Naws = DataSource.getDataFeed('NAWS');

  const handlePresetDateChange = (newPresetDate: PresetDate) => {
    setPresetDate(newPresetDate);
  };

  const { startDate, endDate } = useMemo(() => {
    const now = moment().utc(); // freeze current time once
    let start: moment.Moment;

    switch (presetDate) {
      case PresetDate.TRAILING_MONTH:
        start = now.clone().subtract(1, 'month');
        break;
      case PresetDate.TRAILING_THREE_MONTHS:
        start = now.clone().subtract(3, 'months');
        break;
      case PresetDate.TRAILING_WEEK:
      default:
        start = now.clone().subtract(1, 'week');
        break;
    }

    return {
      startDate: start.toISOString(),
      endDate: now.toISOString(),
    };
  }, [presetDate]);

  // Compute training statistics
  const computedStats = useMemo((): ComputedTrainingStats | null => {
    if (!trainingStats) {
      return null;
    }

    const { overallStats, directReportsStats } = trainingStats;

    // Training Statuses (overall numbers) - ignoring pendingAssignTrainings
    const trainingStatuses = {
      completed: overallStats.completedTrainings,
      inProgress: overallStats.inProgressTrainings,
      overdue: overallStats.overdueTrainings,
      assigned: overallStats.assignedTrainings,
    };

    // Associate progress percentages
    const totalAssociates = directReportsStats.length;

    if (totalAssociates === 0) {
      return {
        trainingStatuses,
        associateProgress: {
          completedAll: 0,
          notStartedAny: 0,
          someInProgress: 0,
          hasOverdue: 0,
        },
      };
    }

    let completedAllCount = 0;
    let notStartedAnyCount = 0;
    let someInProgressCount = 0;
    let hasOverdueCount = 0;

    directReportsStats.forEach(associate => {
      const { stats } = associate;

      // Check if completed all (has completed trainings and no in-progress/overdue)
      if (
        stats.completedTrainings > 0 &&
        stats.inProgressTrainings === 0 &&
        stats.overdueTrainings === 0
      ) {
        completedAllCount++;
      }

      // Check if hasn't started any (no completed/in-progress/overdue trainings)
      if (
        stats.completedTrainings === 0 &&
        stats.inProgressTrainings === 0 &&
        stats.overdueTrainings === 0 &&
        stats.assignedTrainings > 0 // Has assigned trainings but hasn't started
      ) {
        notStartedAnyCount++;
      }

      // Check if has some in progress
      if (stats.inProgressTrainings > 0) {
        someInProgressCount++;
      }

      // Check if has overdue trainings
      if (stats.overdueTrainings > 0) {
        hasOverdueCount++;
      }
    });

    const associateProgress = {
      completedAll: Math.round((completedAllCount / totalAssociates) * 100),
      notStartedAny: Math.round((notStartedAnyCount / totalAssociates) * 100),
      someInProgress: Math.round((someInProgressCount / totalAssociates) * 100),
      hasOverdue: Math.round((hasOverdueCount / totalAssociates) * 100),
    };

    return {
      trainingStatuses,
      associateProgress,
    };
  }, [trainingStats]);

  useEffect(() => {
    if (
      inspireOptions.state === DataState.LOADED &&
      profile.directReports?.length > 0 &&
      startDate &&
      endDate
    ) {
      const prefix = cscEntityPrefix + ':';
      const variables: GetTrainingStatsInput = {
        userID: `${prefix}${profile.user.userID}`,
        users: profile.directReports.map(user => `${prefix}${user.userID}`),
        startDate,
        endDate,
      };
      apiNaws.getTrainingStats(
        variables,
        setTrainingStats,
        setTrainingStatsState
      );
    }
  }, [
    inspireOptions.state,
    profile.user.userID,
    profile.directReports,
    startDate,
    endDate,
    cscEntityPrefix,
  ]);

  const renderCardContent = () => {
    if (trainingStatsState === DataState.LOADING) {
      return (
        <Box spacingInset='400 none none none'>
          <Row alignmentHorizontal='center'>
            <Loader type='circular' size='large' />
          </Row>
        </Box>
      );
    }

    if (trainingStatsState === DataState.ERROR) {
      return (
        <Box spacingInset='400 none none none'>
          <Alert type='error'>
            <FormattedMessage
              id='cs_perfportal_error_reason_1'
              defaultMessage='Your credentials may be incorrect or inadequate.'
            />
          </Alert>
        </Box>
      );
    }

    if (!computedStats) {
      return (
        <Box spacingInset='400 none none none'>
          <Text>
            <FormattedMessage
              id='cs_perfportal_training_no_data'
              defaultMessage='No training data available.'
            />
          </Text>
        </Box>
      );
    }

    const { trainingStatuses, associateProgress } = computedStats;

    const donutData = [
      {
        value: trainingStatuses.completed,
        key: 'completed',
        label: intl.formatMessage({
          id: 'cs_perfportal_training_completed',
          defaultMessage: 'Completed',
        }),
      },
      {
        value: trainingStatuses.assigned,
        key: 'notStarted',
        label: intl.formatMessage({
          id: 'cs_perfportal_training_not_started',
          defaultMessage: 'Not started',
        }),
      },
      {
        value: trainingStatuses.inProgress,
        key: 'inProgress',
        label: intl.formatMessage({
          id: 'cs_perfportal_training_in_progress',
          defaultMessage: 'In progress',
        }),
      },
      {
        value: trainingStatuses.overdue,
        key: 'overdue',
        label: intl.formatMessage({
          id: 'cs_perfportal_training_overdue',
          defaultMessage: 'Overdue',
        }),
      },
    ];

    const totalTrainings =
      trainingStatuses.completed +
      trainingStatuses.assigned +
      trainingStatuses.inProgress +
      trainingStatuses.overdue;

    return (
      <>
        <Row spacingInset='400' alignmentHorizontal='justify'>
          <Column>
            {/* Associate Progress Section */}
            <Box spacingInset='400 none none none'>
              <Box spacingInset='0 0 300 0'>
                <Text type='h400'>
                  <FormattedMessage
                    id='cs_perfportal_training_associate_progress'
                    defaultMessage='Associate Progress'
                  />
                </Text>
              </Box>
              <Column spacing='300'>
                <Text color='secondary' type='b500'>
                  <FormattedMessage
                    id='cs_perfportal_training_completed_all_desc'
                    defaultMessage='{percentage}% have completed all their trainings'
                    values={{ percentage: associateProgress.completedAll }}
                  />
                </Text>
                <Text color='secondary' type='b500'>
                  <FormattedMessage
                    id='cs_perfportal_training_not_started_any_desc'
                    defaultMessage="{percentage}% haven't started any trainings"
                    values={{ percentage: associateProgress.notStartedAny }}
                  />
                </Text>
                <Text color='secondary' type='b500'>
                  <FormattedMessage
                    id='cs_perfportal_training_some_in_progress_desc'
                    defaultMessage='{percentage}% have some trainings still in progress'
                    values={{ percentage: associateProgress.someInProgress }}
                  />
                </Text>
                <Text color='secondary' type='b500'>
                  <FormattedMessage
                    id='cs_perfportal_training_has_overdue_desc'
                    defaultMessage='{percentage}% have overdue trainings'
                    values={{ percentage: associateProgress.hasOverdue }}
                  />
                </Text>
              </Column>
            </Box>
          </Column>

          <Column>
            {/* Training Status Numbers */}
            <Box spacingInset='400 none none none'>
              <LegendProvider
                data={donutData}
                aria-labelledby='training-status-donut'
              >
                <Row
                  spacing='500'
                  alignmentHorizontal='center'
                  alignmentVertical='center'
                >
                  {totalTrainings > 0 ? (
                    <>
                      <Pie width={300} data={donutData} donutRatio={0.7}>
                        <Text type='d100' tag='div'>
                          {totalTrainings}
                        </Text>
                        <Text type='h100' tag='div' color='secondary'>
                          <FormattedMessage
                            id='cs_perfportal_training_assigned_label'
                            defaultMessage='Training assigned'
                          />
                        </Text>
                      </Pie>
                      <Column spacing='200'>
                        <Box spacingInset='0 0 300 0'>
                          <Heading id='training-status-donut' level={4}>
                            <FormattedMessage
                              id='cs_perfportal_training_statuses_title'
                              defaultMessage='Training statuses'
                            />
                          </Heading>
                        </Box>
                        <Legend
                          direction='vertical'
                          values={item => item.value.toLocaleString()}
                        />
                      </Column>
                    </>
                  ) : (
                    <Text type='b400' color='secondary'>
                      <FormattedMessage
                        id='cs_perfportal_training_no_data_for_time_range'
                        defaultMessage='No training data available for this time range.'
                      />
                    </Text>
                  )}
                </Row>
              </LegendProvider>
            </Box>
          </Column>
        </Row>
      </>
    );
  };

  return (
    <Column spacing='500'>
      {/* Training Statuses Section */}
      <Card spacingInset='400' min-height='900px'>
        <Row spacingInset='300' alignmentHorizontal='justify'>
          <Column>
            <Box spacingInset='0'>
              <Heading level={2} type='h400'>
                <FormattedMessage
                  id='cs_perfportal_training_completion_rate_title'
                  defaultMessage='Training completion rates'
                />
              </Heading>
              <Text color='secondary'>
                <FormattedMessage
                  id='cs_perfportal_training_aggregated_progress_subtitle'
                  defaultMessage='Aggregated progress for training {timeRange} ({startDate} to {endDate})'
                  values={{
                    timeRange: intl.formatMessage({
                      id: `cs_perfportal_training_${presetDate.toLowerCase()}`,
                      defaultMessage: presetDate
                        .replaceAll('_', ' ')
                        .toLowerCase(),
                    }),
                    startDate: moment(startDate).format('MMM D, YYYY'),
                    endDate: moment(endDate).isSame(moment(), 'day')
                      ? intl.formatMessage({
                          id: `cs_perfportal_training_today`,
                          defaultMessage: 'today',
                        })
                      : moment(endDate).format('MMM D, YYYY'),
                  }}
                />
              </Text>
            </Box>
          </Column>
          <Column>
            <ButtonGroup value={presetDate} onChange={handlePresetDateChange}>
              <ButtonOption value={PresetDate.TRAILING_WEEK} disabled={false}>
                <FormattedMessage
                  id='cs_perfportal_training_trailing_week'
                  defaultMessage='Trailing week'
                />
              </ButtonOption>
              <ButtonOption value={PresetDate.TRAILING_MONTH} disabled={false}>
                <FormattedMessage
                  id='cs_perfportal_training_trailing_month'
                  defaultMessage='Trailing month'
                />
              </ButtonOption>
              <ButtonOption
                value={PresetDate.TRAILING_THREE_MONTHS}
                disabled={false}
              >
                <FormattedMessage
                  id='cs_perfportal_training_trailing_three_months'
                  defaultMessage='Trailing 3 months'
                />
              </ButtonOption>
            </ButtonGroup>
          </Column>
        </Row>

        {renderCardContent()}
      </Card>
    </Column>
  );
};

export default TrainingStats;
